import { BookingStatus } from '@massilia/prisma-generated/enums';
import { z } from 'zod';
import { isoDateString } from '../_shared/dates.js';
import { withRenamedDeprecatedKey } from '../_shared/deprecated-key.js';
// `installmentPaymentDeadline` est l'ancien nom de `paymentDeadline` : alias déprécié,
// à supprimer à la release suivante.
export const updateBookingConditionsSchema = withRenamedDeprecatedKey(z.object({
    // `nullish` et non `optional` : le formulaire d'échéances envoie toujours les deux
    // dates, à `null` quand l'agent les efface ou qu'elles n'ont jamais été saisies.
    // `updateConditions` sait déjà effacer sur `null` — seule l'entrée le refusait, et
    // tout enregistrement d'un dossier sans date limite d'acompte partait en 400.
    // La clé absente reste absente : la distinction « non fourni » / « effacé », sur
    // laquelle repose le service, est intacte.
    depositDeadline: isoDateString().nullish(),
    installmentChosen: z.boolean().optional(),
    installmentMinimumDepositAmount: z.coerce.number().nullish(),
    paymentDeadline: isoDateString().nullish(),
}), 'installmentPaymentDeadline', 'paymentDeadline');
/**
 * Conditions d'échange et de remboursement d'UN voyage. Elles appartiennent au billet,
 * donc au PNR : deux voyages d'un même dossier n'ont ni la même compagnie ni les mêmes
 * règles. Les quatre champs partent ensemble, c'est ce que saisit le formulaire.
 */
export const updateTripConditionsSchema = z.object({
    exchangeAllowed: z.boolean(),
    exchangeFeeDescription: z.string(),
    refundAllowed: z.boolean(),
    refundFeeDescription: z.string(),
});
/**
 * Coût Amadeus d'UN voyage, en centimes, saisi par l'agent quand la TST ment. `null`
 * rend la main à Amadeus : le coût est relu chez Affidium sur-le-champ.
 */
export const updateTripAmadeusPriceSchema = z.object({
    amadeusPriceCents: z.number().int().nonnegative().nullable(),
});
/** Notes internes du dossier, réservées aux agents. Chaîne vide ou `null` efface. */
export const updateBookingAgentNotesSchema = z.object({
    agentNotes: z.string().max(10000).nullable(),
});
/** Voucher d'un séjour sans PNR, déjà déposé sur S3 (`POST /file`). `null` le retire. */
export const updateBookingVoucherSchema = z.object({
    voucher: z.object({ fileId: z.string().min(1), fileName: z.string().min(1).max(255) }).nullable(),
});
export const updateBookingStatusSchema = z.object({
    status: z.nativeEnum(BookingStatus),
});
// Ajout d'un voyage (2e PNR) à un dossier existant : la référence Affidium suffit,
// tout le reste est extrait de la réservation fournisseur.
export const addBookingTripSchema = z.object({
    affidiumReference: z.string().trim().min(1),
});
/**
 * Opération d'assurance ciblant UN voyage (arbitrage n°5). `tripId` absent = le voyage
 * principal : c'est ce qu'envoie un dossier mono-voyage, et le comportement d'avant le
 * lot 5.
 */
export const bookingInsuranceTripSchema = z.object({
    tripId: z.string().optional(),
});
/** Les voyages cochés par l'agent : un contrat Assurever par voyage. */
export const addBookingInsuranceSchema = z.object({
    tripIds: z.array(z.string()).optional(),
});
/**
 * Code promo appliqué à un dossier DÉJÀ RÉSERVÉ, depuis le back-office. Distinct de
 * `applyPromoCodeSchema` (session de checkout) : ici le dossier existe, il peut déjà
 * être payé, et l'écart doit être régularisé.
 */
export const applyBookingPromoCodeSchema = z.object({
    code: z.string().min(1),
    // Absent ou `true` : si le dossier ressort surpayé, l'avoir correspondant est créé
    // dans la foulée. `false` laisse l'agent régulariser à la main depuis l'onglet
    // Paiements — le geste reste le sien, on ne fait que le proposer par défaut.
    createCreditNote: z.boolean().optional(),
});
export const updateBookingInsurancePriceSchema = z.object({
    tripId: z.string().optional(),
    price: z.number().min(0),
});
export const createBookingExtraSchema = z
    .object({
    bookingId: z.string().min(1),
    fileId: z.string().optional(),
    fileName: z.string().optional(),
    name: z.string().min(1),
    providerName: z.string().trim(),
    // Modification de billet : coût déjà compris dans le tarif Amadeus, retiré du coût vol
    // pour la marge vol.
    costIncludedInAmadeus: z.boolean().optional(),
    description: z.string().optional(),
    fileType: z.string().optional(),
    // Coût prestataire, requis à la création : sans lui la prestation entre dans
    // le dossier avec une marge inconnue, et rien ne rappellera plus de la
    // renseigner. Les prestations antérieures au suivi restent à NULL en base —
    // c'est le seul cas d'absence qu'on accepte.
    netAmountCents: z.number().min(0),
    price: z.number().min(0),
    providerExternalRef: z.string().trim(),
})
    // Le prestataire d'une modification de billet est la compagnie : facultatif.
    .superRefine((extra, ctx) => {
    if (extra.costIncludedInAmadeus)
        return;
    for (const key of ['providerName', 'providerExternalRef']) {
        if (!extra[key])
            ctx.addIssue({ code: 'custom', message: 'Requis', path: [key] });
    }
});
export const updateBookingExtraSchema = z.object({
    fileId: z.string().optional(),
    fileName: z.string().optional(),
    name: z.string().optional(),
    providerName: z.string().trim().optional(),
    costIncludedInAmadeus: z.boolean().optional(),
    description: z.string().nullish(),
    fileType: z.string().optional(),
    // Optionnel comme tous les champs de ce PATCH partiel, mais jamais annulable :
    // pas de `nullish()`. Un net effacé rendrait une marge déjà mesurée à
    // l'inconnu, ce que le formulaire n'a aucune raison de demander.
    netAmountCents: z.number().min(0).optional(),
    price: z.number().min(0).optional(),
    providerExternalRef: z.string().trim().optional(),
});
